/**
 * An automatic message's body (Gabriel 2026-10-05, one rule for every automatic message): whole up
 * to about 15 lines; a longer one clamps with a fade and Show more, which opens it in place.
 * Shared by the admin and client chat threads; each skin styles the body and the Show more control.
 */
import { useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import styles from './ClampedBody.module.css';

interface ClampedBodyProps {
  /** The body's own skin (white-space, link color). */
  className: string;
  /** The Show more control's skin. */
  moreClassName: string;
  /** What the body shows: measured again when it changes. */
  measureKey: unknown;
  onClick?: (event: MouseEvent) => void;
  children: ReactNode;
}

export function ClampedBody({ className, moreClassName, measureKey, onClick, children }: ClampedBodyProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (body) setOverflowing(body.scrollHeight > body.clientHeight + 1);
  }, [measureKey]);

  return (
    <>
      <div
        className={`${styles.clamp} ${className}`}
        ref={bodyRef}
        data-overflowing={overflowing || undefined}
        data-expanded={expanded || undefined}
        onClick={onClick}
      >
        {children}
      </div>
      {overflowing && !expanded && (
        <button
          type="button"
          className={moreClassName}
          onClick={(event) => {
            event.stopPropagation(); // the bubble itself opens its subject
            setExpanded(true);
          }}
        >
          Show more
        </button>
      )}
    </>
  );
}
